// 010B — Laboratorio Clínico Informe (Anexo 1 MSP, SNS-MSP/HCU-form.010B/2021). Fase 7, Tanda 15: hoja 1 con los parámetros
// fijos impresos (hematológico, coprológico, uroanálisis, química, serología, bacteriología, otros) y hoja 2 con las 17
// secciones de filas libres, cada fila con Examen / Resultado / Unidad / Valor de referencia. Catálogo y decisiones en
// `bloques010BDatos.ts`. Se conserva un `Observation` por resultado y el `DiagnosticReport` que los agrupa (`LabsTab`).
import { useMedplum } from '@medplum/react-hooks';
import type { Observation, Reference, ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional, idTemporal } from './transaccionFhir';
import { Bloque, CamposSimples } from './camposSimples';
import { CATEGORIA_LABORATORIO } from './categoriasDiagnosticas';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { SelectorRecurso } from './SelectorRecurso';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_SERVICIO_010B,
  columnasDeSeccion,
  estadoInicial010B,
  filasConResultado,
  filasDeSeccion,
  paresDe010B,
  SECCIONES_HOJA1_010B,
  SECCIONES_HOJA2_010B,
  valorConUnidad,
  type Estado010B,
  type SeccionResultados,
} from './bloques010BDatos';

export function Formulario010B({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [solicitud, setSolicitud] = useState<ServiceRequest>();
  const [estado, setEstado] = useState<Estado010B>(() => estadoInicial010B());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '010B', t('formularios.f010b.titulo'), { servicio: estado.servicio.servicio });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();
      const lote = new LoteTransaccional();
      const referencias: Reference<Observation>[] = [];
      for (const f of filasConResultado(estado)) {
        const urlObs = idTemporal();
        lote.crear<Observation>({
          resourceType: 'Observation',
          status: 'final',
          subject,
          encounter,
          code: { text: `${f.seccion}: ${f.examen || 'Sin rótulo'}` },
          valueString: valorConUnidad(f) || undefined,
          referenceRange: f.referencia ? [{ text: f.referencia }] : undefined,
          effectiveDateTime: ahora,
        }, urlObs);
        referencias.push({ reference: urlObs });
      }
      lote.crear({
        resourceType: 'DiagnosticReport',
        status: 'final',
        category: [CATEGORIA_LABORATORIO],
        code: solicitud?.code ?? { text: CATEGORIA_LABORATORIO.text },
        subject,
        encounter,
        basedOn: solicitud?.id ? [{ reference: `ServiceRequest/${solicitud.id}` }] : undefined,
        effectiveDateTime: ahora,
        issued: ahora,
        result: referencias.length ? referencias : undefined,
      });
      await lote.enviar(medplum);
      const pares = [...(solicitud ? [{ pregunta: t('formularios.f010b.solicitudOrigen'), respuesta: solicitud.code?.text ?? solicitud.id ?? '' }] : []), ...paresDe010B(estado)];
      onGuardado(pares, datosEncabezadoMsp);
    });

  const seccion = (sec: SeccionResultados): JSX.Element => (
    <div key={sec.clave} className="flex flex-col gap-1">
      <p className="text-sm font-medium text-hce-ink">{sec.etiqueta}</p>
      <TablaConColumnas
        filas={filasDeSeccion(sec)}
        columnas={columnasDeSeccion(sec)}
        estado={estado.resultados[sec.clave] ?? {}}
        onChange={(tabla) => setEstado({ ...estado, resultados: { ...estado.resultados, [sec.clave]: tabla } })}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f010b.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <SelectorRecurso<'ServiceRequest'>
        resourceType="ServiceRequest"
        subject={subject}
        label={t('formularios.f010b.solicitudOrigen')}
        etiqueta={(r) => r.code?.text ?? r.id ?? ''}
        value={solicitud}
        onChange={setSolicitud}
        filtro={{ category: 'http://terminology.hl7.org/CodeSystem/v2-0074|LAB' }}
      />

      <Bloque titulo={t('formularios.f010b.servicioTitulo')}>
        <SelectorDiagnosticoCie10 label={t('formularios.f010b.diagnostico')} value={estado.diagnostico} onChange={(v) => setEstado({ ...estado, diagnostico: v })} />
        <CamposSimples prefijoId="f010b-srv" campos={CAMPOS_SERVICIO_010B} valores={estado.servicio} columnas={3} onChange={(v) => setEstado({ ...estado, servicio: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f010b.resultadosTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f010b.resultadosNota')}</p>
        {SECCIONES_HOJA1_010B.map(seccion)}
      </Bloque>

      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium text-hce-ink">{t('formularios.f010b.hoja2Titulo')}</summary>
        <div className="mt-3 flex flex-col gap-4">{SECCIONES_HOJA2_010B.map(seccion)}</div>
      </details>

      <Bloque titulo={t('formularios.f010b.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
